/**
 * UAE-appropriate default chart of accounts (ARCHITECTURE §3.1).
 *
 * VAT input/output control accounts and an FX gain/loss account are pre-wired,
 * plus intercompany control accounts that consolidation eliminates. A tenant
 * can extend or replace this; it is data, not logic.
 */
import type { Account, AccountType } from "./types";

export const DEFAULT_CHART: readonly Account[] = [
  { code: "1100", name: "Bank — AED current", type: "asset" },
  { code: "1105", name: "Bank — USD account", type: "asset" },
  { code: "1200", name: "Accounts receivable", type: "asset" },
  { code: "1450", name: "VAT input control", type: "asset", taxCode: "SR" },
  { code: "1500", name: "Intercompany receivable", type: "asset", intercompany: true },
  { code: "2100", name: "VAT output control", type: "liability", taxCode: "SR" },
  { code: "2200", name: "Accounts payable", type: "liability" },
  { code: "2500", name: "Intercompany payable", type: "liability", intercompany: true },
  { code: "2600", name: "Corporate tax provision", type: "liability" },
  { code: "3000", name: "Share capital", type: "equity" },
  { code: "3900", name: "Retained earnings", type: "equity" },
  { code: "4000", name: "Sales revenue", type: "income", taxCode: "SR" },
  { code: "4900", name: "Intercompany income", type: "income", intercompany: true },
  { code: "5000", name: "Cost of sales", type: "expense", taxCode: "SR" },
  { code: "6000", name: "Operating expenses", type: "expense", taxCode: "SR" },
  { code: "6500", name: "Intercompany expense", type: "expense", intercompany: true },
  { code: "7800", name: "FX gain / (loss)", type: "income", taxCode: "OS" },
  { code: "8000", name: "Corporate tax expense", type: "expense", taxCode: "OS" },
];

/** Debit-normal account types increase with debits; the rest with credits. */
export function isDebitNormal(type: AccountType): boolean {
  return type === "asset" || type === "expense";
}

/** Index a chart by code for O(1) lookup. */
export function indexAccounts(
  accounts: readonly Account[],
): ReadonlyMap<string, Account> {
  return new Map(accounts.map((a) => [a.code, a]));
}

const LABELS: Record<AccountType, string> = {
  asset: "Asset",
  liability: "Liability",
  equity: "Equity",
  income: "Income",
  expense: "Expense",
};

export function accountTypeLabel(type: AccountType): string {
  return LABELS[type];
}
